import useFormatNumber from "core/utils/formatNumber";


const expandableColumnTreeMap = (data) => {
    const formatNumber = useFormatNumber();
    console.log("tree:::DATA>>>", data);

    const tree = {};


    tree["parent_source_name"] = data?.final_output_variable_name || "Unknown"

    // Helper function to format any value that looks like a number
    const formatValue = (value) => {
        if (value === null || value === undefined || value === "null") {
            return "-";
        }

        // Convert to string first to handle scientific notation like "4.0E7"
        const strValue = String(value).trim();
        const numValue = Number(strValue);  // Number("4.0E7") = 40000000 ✅

        if (!isNaN(numValue) && strValue !== '') {
            return formatNumber(numValue, true);
        }

        return value;
    };
    // Helper function to recursively format all values in an object
    const formatObjectValues = (obj) => {
        if (!obj || typeof obj !== 'object') return obj;

        if (Array.isArray(obj)) {
            return obj.map(item => formatObjectValues(item));
        }

        const formatted = {};
        for (const [key, val] of Object.entries(obj)) {
            if (val && typeof val === 'object') {
                formatted[key] = formatObjectValues(val);
            } else {
                formatted[key] = formatValue(val);
            }
        }
        return formatted;
    };

    // Helper function to format inputs array
    const formatInputs = (inputs) => {
        if (!inputs || !Array.isArray(inputs)) return inputs;

        return inputs.map(item => {
            const formattedItem = {};
            for (const [key, value] of Object.entries(item)) {
                formattedItem[key] = formatValue(value);
            }
            return formattedItem;
        });
    };

    // Display the final_output_formula first
    let formulaString = data?.final_output_formula?.formula || "";
    if (typeof formulaString === 'string' && formulaString.trim()) {
        tree["parent_formula"] = formulaString;
    }

    tree["output"] = formatValue(data?.final_output_formula?.output);
    tree["children"] = [];

    const processChildrenInputs = (inputData, treeNode, matchedDecision) => {
        inputData.forEach((inputItem) => {
            const nodeName = Object.keys(inputItem)[0];
            const nodeValue = inputItem[nodeName];

            // Format the display value
            const displayValue = formatValue(nodeValue);

            let decision = {};
            decision["decisionId"] = matchedDecision?.decisionId;
            decision["decisionName"] = matchedDecision?.decisionName;
            decision["decisionOutput"] = formatValue(matchedDecision?.decisionOutput);
            decision["decisionVersion"] = matchedDecision?.decisionVersion;
            decision["rulePlan"] = matchedDecision?.rulePlan;

            if (matchedDecision?.decisionInputs?.length > 0) {
                decision["decisionInput"] = formatInputs(matchedDecision.decisionInputs);
            }

            const isKeyMatching = matchedDecision?.decisionOutputVariables?.some(variable =>
                inputData.some(item => Object.keys(item).includes(variable.outputName))
            );

            let childNode = {
                source_name: nodeName,
                output: displayValue,
                decision: isKeyMatching ? decision : {}
            };
            treeNode.children.push(childNode);
        });
    }

    // Recursive function to process the inputs
    const processInputs = (inputData, treeNode) => {
        inputData.forEach((inputItem) => {
            const nodeName = Object.keys(inputItem)[0];
            const nodeValue = inputItem[nodeName];

            let childNode = {
                source_name: nodeName,
                value: formatValue(nodeValue)
            };

            // Find the matching decision output for ElseJsonLogic_formula
            let matchedDecision = data.decisionOutputs.find((decisionItem) => {
                const decisionItemKeys = Object.keys(decisionItem);

                if (decisionItemKeys.includes(nodeName)) {
                    return decisionItem;
                } else if (decisionItem?.output_formula?.variable === nodeName) {
                    return decisionItem;
                }
            });

            if (matchedDecision) {
                childNode = {
                    ...childNode,
                    ruleIndex: matchedDecision?.ruleIndex,
                    rulePlan: matchedDecision?.rulePlan,
                    formula: matchedDecision?.output_formula?.formula || matchedDecision?.ElseJsonLogic_formula?.formula,
                    output: formatValue(matchedDecision?.output_formula?.output),
                    decision: {}
                };

                childNode.decision["decisionId"] = matchedDecision?.decisionId;
                childNode.decision["decisionName"] = matchedDecision?.decisionName;
                childNode.decision["decisionOutput"] = formatValue(matchedDecision?.decisionOutput);
                childNode.decision["decisionVersion"] = matchedDecision?.decisionVersion;
                childNode.decision["rulePlan"] = matchedDecision?.rulePlan;

                // Process inputs recursively if available
                if (matchedDecision?.inputs?.length > 0) {
                    childNode["children"] = [];
                    processChildrenInputs(matchedDecision?.inputs, childNode, matchedDecision);
                } else if (matchedDecision?.ElseJsonLogic_formula?.inputs?.length > 0) {
                    childNode["children"] = [];
                    processInputs(matchedDecision.ElseJsonLogic_formula?.inputs, childNode);
                } else if (matchedDecision?.output_formula?.inputs?.length > 0) {
                    childNode["children"] = [];
                    processChildrenInputs(matchedDecision?.output_formula?.inputs, childNode, matchedDecision);
                } else {
                    childNode["children"] = [];
                }
            }
            treeNode.children.push(childNode);
        });
    };

    // Display ElseJsonLogic_formula if available
    let elseFormula = data?.decisionOutputs?.find((d) => d.ElseJsonLogic_formula);
    if (elseFormula) {
        let elseFormulaString = elseFormula?.ElseJsonLogic_formula?.formula || "";
        if (typeof elseFormulaString === 'string' && elseFormulaString.trim()) {

            // Add the ElseJsonLogic_formula as a child node of the main tree
            let elseFormulaNode = {
                source_name: data?.final_output_formula?.formula,
                formula: elseFormulaString,
                output: formatValue(elseFormula?.ElseJsonLogic_formula?.output),
                children: [],
                decision: {}
            };

            elseFormulaNode.decision["decisionId"] = elseFormula?.decisionId;
            elseFormulaNode.decision["decisionName"] = elseFormula?.decisionName;
            elseFormulaNode.decision["decisionOutput"] = formatValue(elseFormula?.decisionOutput);
            elseFormulaNode.decision["decisionVersion"] = elseFormula?.decisionVersion;

            if (elseFormula?.decisionInputs?.length > 0) {
                elseFormulaNode.decision["decisionInputs"] = formatInputs(elseFormula.decisionInputs);
            }

            // Process the inputs for ElseJsonLogic_formula recursively
            if (elseFormula?.inputs?.length > 0) {
                processInputs(elseFormula.inputs, elseFormulaNode);
            }

            if (elseFormulaNode.decision["decisionId"] !== 'NA') {
                // Add this node to the main tree's children
                tree.children.push(elseFormulaNode);
            }
        }
    }

    // Process final_output_formula inputs recursively
    if (data?.final_output_formula?.inputs?.length > 0) {
        processInputs(data.final_output_formula.inputs, tree);
    } else if (data?.decisionOutputs) {
        // Process inputs from decisionOutputs if final_output_formula inputs are not available
        data.decisionOutputs.forEach((decision) => {
            if (decision?.output_formula?.inputs?.length > 0) {
                processInputs(decision.output_formula.inputs, tree);
            } else if (decision?.ElseJsonLogic_formula?.inputs?.length > 0) {
                processInputs(decision.ElseJsonLogic_formula.inputs, tree);
            }
        });
    }

    // Final pass to ensure all values in the tree are formatted
    const finalTree = formatObjectValues(tree);

    console.log("tree:::", finalTree);
    return finalTree;
};

export default expandableColumnTreeMap;